import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import MultiSelectFilter from './MultiSelectFilter';
import api from '../lib/api';

const fmt = (v) => {
  if (v === undefined || v === null) return '0';
  if (typeof v !== 'number') return v;
  return v.toLocaleString('en-IN', { maximumFractionDigits: 0 });
};

const Shortfall = ({ user }) => {
  const [activeTab, setActiveTab] = useState('so');
  const [loading, setLoading] = useState(false);
  const [soData, setSoData] = useState(null);
  const [designData, setDesignData] = useState(null);
  const [search, setSearch] = useState('');

  // Filters
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [filterCustomers, setFilterCustomers] = useState([]);
  const [filterBrands, setFilterBrands] = useState([]);
  const [filterAgents, setFilterAgents] = useState([]);
  const [filterDistributors, setFilterDistributors] = useState([]);
  const [filterVendors, setFilterVendors] = useState([]);

  // Filter options
  const [customers, setCustomers] = useState([]);
  const [brands, setBrands] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [filterOpts, setFilterOpts] = useState({ agents: [], distributors: [] });

  // Expandable rows (SO-wise)
  const [expandedOrders, setExpandedOrders] = useState({});
  // Expandable rows (Design-wise)
  const [expandedDesigns, setExpandedDesigns] = useState({});
  const [lightboxImg, setLightboxImg] = useState(null);

  const API_BASE = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    Promise.all([
      api.get('/customers'),
      api.get('/brands'),
      api.get('/vendors'),
      api.get('/reports/filter-options'),
    ]).then(([c, b, v, fo]) => {
      setCustomers(c.data || []);
      setBrands(b.data || []);
      setVendors(v.data || []);
      setFilterOpts(fo.data || { agents: [], distributors: [] });
    }).catch(() => {});
  }, []);

  const buildParams = useCallback(() => {
    const p = new URLSearchParams();
    if (dateFrom) p.append('date_from', dateFrom);
    if (dateTo) p.append('date_to', dateTo);
    if (filterCustomers.length) p.append('customer_id', filterCustomers.join(','));
    if (filterBrands.length) p.append('brand_id', filterBrands.join(','));
    if (filterAgents.length) p.append('agent_id', filterAgents.join(','));
    if (filterDistributors.length) p.append('distributor_id', filterDistributors.join(','));
    if (filterVendors.length) p.append('vendor_id', filterVendors.join(','));
    return p.toString();
  }, [dateFrom, dateTo, filterCustomers, filterBrands, filterAgents, filterDistributors, filterVendors]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = buildParams();
      if (activeTab === 'so') {
        const res = await api.get(`/shortfall/so-wise?${params}`);
        setSoData(res.data);
      } else {
        const res = await api.get(`/shortfall/design-wise?${params}`);
        setDesignData(res.data);
      }
    } catch {
      toast.error('Failed to load shortfall data');
    } finally {
      setLoading(false);
    }
  }, [activeTab, buildParams]);

  useEffect(() => { loadData(); }, [loadData]);

  const customerOpts = customers.map(c => ({ value: c.id, label: c.company_name || c.contact_person || c.id }));
  const brandOpts = brands.map(b => ({ value: b.id, label: b.name }));
  const vendorOpts = vendors.map(v => ({ value: v.id, label: v.name || v.company_name || v.id }));
  const agentOpts = (filterOpts.agents || []).map(a => ({ value: a.id, label: a.name || a.id }));
  const distributorOpts = (filterOpts.distributors || []).map(d => ({ value: d.id, label: d.name || d.id }));

  const hasAnyFilter = dateFrom || dateTo || filterCustomers.length || filterBrands.length || filterAgents.length || filterDistributors.length || filterVendors.length;

  const clearFilters = () => {
    setDateFrom(''); setDateTo('');
    setFilterCustomers([]); setFilterBrands([]);
    setFilterAgents([]); setFilterDistributors([]);
    setFilterVendors([]);
  };

  const summary = activeTab === 'so' ? soData?.summary : designData?.summary;

  return (
    <>
    <div className="space-y-4 p-6" data-testid="shortfall-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Nunito' }} data-testid="shortfall-title">Shortfall</h1>
        <p className="text-sm text-gray-500">Track pending SO items and stock shortfall to follow up with vendors</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-fit" data-testid="shortfall-tabs">
        <button
          onClick={() => { setActiveTab('so'); setSearch(''); setExpandedOrders({}); }}
          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${activeTab === 'so' ? 'bg-orange-600 text-white shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
          data-testid="tab-so-wise"
        >SO-wise</button>
        <button
          onClick={() => { setActiveTab('design'); setSearch(''); setExpandedDesigns({}); }}
          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${activeTab === 'design' ? 'bg-orange-600 text-white shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
          data-testid="tab-design-wise"
        >Design-wise</button>
      </div>

      {/* Filters */}
      <Card data-testid="shortfall-filters"><CardContent className="p-4">
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <Label className="text-xs mb-1 block">From</Label>
            <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="h-8 text-xs w-36" data-testid="shortfall-date-from" />
          </div>
          <div>
            <Label className="text-xs mb-1 block">To</Label>
            <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="h-8 text-xs w-36" data-testid="shortfall-date-to" />
          </div>
          <div className="min-w-[160px]">
            <Label className="text-xs mb-1 block">Customer</Label>
            <MultiSelectFilter label="All Customers" options={customerOpts} selected={filterCustomers} onChange={setFilterCustomers} />
          </div>
          <div className="min-w-[130px]">
            <Label className="text-xs mb-1 block">Brand</Label>
            <MultiSelectFilter label="All Brands" options={brandOpts} selected={filterBrands} onChange={setFilterBrands} />
          </div>
          <div className="min-w-[130px]">
            <Label className="text-xs mb-1 block">Vendor</Label>
            <MultiSelectFilter label="All Vendors" options={vendorOpts} selected={filterVendors} onChange={setFilterVendors} />
          </div>
          <div className="min-w-[130px]">
            <Label className="text-xs mb-1 block">Agent</Label>
            <MultiSelectFilter label="All Agents" options={agentOpts} selected={filterAgents} onChange={setFilterAgents} />
          </div>
          <div className="min-w-[130px]">
            <Label className="text-xs mb-1 block">Distributor</Label>
            <MultiSelectFilter label="All Distributors" options={distributorOpts} selected={filterDistributors} onChange={setFilterDistributors} />
          </div>
          {hasAnyFilter && <Button variant="ghost" size="sm" className="text-red-500 h-8" onClick={clearFilters}>Clear</Button>}
        </div>
      </CardContent></Card>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="shortfall-summary">
          <Card className="border-l-4 border-l-blue-500"><CardContent className="p-3">
            <p className="text-xs text-gray-500">{activeTab === 'so' ? 'Open SOs' : 'Designs'}</p>
            <p className="text-2xl font-bold text-blue-700">{fmt(activeTab === 'so' ? summary.total_orders : summary.total_designs)}</p>
          </CardContent></Card>
          <Card className="border-l-4 border-l-amber-500"><CardContent className="p-3">
            <p className="text-xs text-gray-500">Total Pending</p>
            <p className="text-2xl font-bold text-amber-700">{fmt(summary.total_pending)}</p>
          </CardContent></Card>
          <Card className="border-l-4 border-l-emerald-500"><CardContent className="p-3">
            <p className="text-xs text-gray-500">In Stock</p>
            <p className="text-2xl font-bold text-emerald-700">{fmt(summary.total_in_stock)}</p>
          </CardContent></Card>
          <Card className="border-l-4 border-l-red-500"><CardContent className="p-3">
            <p className="text-xs text-gray-500">Shortfall</p>
            <p className="text-2xl font-bold text-red-700">{fmt(summary.total_shortfall)}</p>
          </CardContent></Card>
          <Card className="border-l-4 border-l-indigo-500"><CardContent className="p-3">
            <p className="text-xs text-gray-500">Fulfillment %</p>
            <p className="text-2xl font-bold text-indigo-700">{summary.fulfillment_pct}%</p>
          </CardContent></Card>
        </div>
      )}

      {/* Search */}
      <div className="flex gap-2 items-center">
        <Input
          value={search} onChange={e => setSearch(e.target.value)}
          placeholder={activeTab === 'so' ? 'Search by SO#, customer, vendor...' : 'Search by design, brand, vendor...'}
          className="max-w-sm h-8 text-sm" data-testid="shortfall-search"
        />
      </div>

      {/* Content */}
      {loading ? (
        <Card><CardContent className="p-12 text-center text-gray-400">Loading shortfall data...</CardContent></Card>
      ) : activeTab === 'so' ? (
        <SOWiseTable data={soData} search={search} expanded={expandedOrders} setExpanded={setExpandedOrders} />
      ) : (
        <DesignWiseTable data={designData} search={search} expanded={expandedDesigns} setExpanded={setExpandedDesigns} lightboxImg={lightboxImg} setLightboxImg={setLightboxImg} />
      )}
    </div>

    {/* Image Lightbox */}
    {lightboxImg && (
      <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center" onClick={() => setLightboxImg(null)}>
        <img src={lightboxImg} alt="Design" className="max-w-[90vw] max-h-[85vh] object-contain rounded-lg shadow-2xl" />
        <button className="absolute top-4 right-4 text-white bg-black/50 rounded-full w-10 h-10 flex items-center justify-center hover:bg-black/70 text-xl"
          onClick={() => setLightboxImg(null)}>✕</button>
      </div>
    )}
    </>
  );
};

/* ======== SO-wise Table ======== */
const SOWiseTable = ({ data, search, expanded, setExpanded }) => {
  const orders = data?.orders || [];
  const s = (search || '').toLowerCase();
  const filtered = orders.filter(o =>
    !s ||
    (o.so_number || '').toLowerCase().includes(s) ||
    (o.customer_name || '').toLowerCase().includes(s) ||
    o.items.some(it => (it.design_code || '').toLowerCase().includes(s) || (it.vendor || '').toLowerCase().includes(s))
  );

  const toggleOrder = (orderId) => {
    setExpanded(prev => ({ ...prev, [orderId]: !prev[orderId] }));
  };

  if (filtered.length === 0) {
    return (
      <Card><CardContent className="p-12 text-center text-gray-400" data-testid="shortfall-empty">
        No pending SO items found. All orders are fulfilled or no data matches the filters.
      </CardContent></Card>
    );
  }

  return (
    <Card data-testid="shortfall-so-table">
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr>
                <th className="p-2.5 text-left font-semibold text-xs text-gray-500 uppercase bg-gray-50 border-b w-8"></th>
                <th className="p-2.5 text-left font-semibold text-xs text-gray-500 uppercase bg-gray-50 border-b">SO #</th>
                <th className="p-2.5 text-left font-semibold text-xs text-gray-500 uppercase bg-gray-50 border-b">Customer</th>
                <th className="p-2.5 text-left font-semibold text-xs text-gray-500 uppercase bg-gray-50 border-b">Agent</th>
                <th className="p-2.5 text-left font-semibold text-xs text-gray-500 uppercase bg-gray-50 border-b">Distributor</th>
                <th className="p-2.5 text-right font-semibold text-xs text-gray-500 uppercase bg-gray-50 border-b">Days</th>
                <th className="p-2.5 text-right font-semibold text-xs text-gray-500 uppercase bg-gray-50 border-b">Pending</th>
                <th className="p-2.5 text-right font-semibold text-xs text-gray-500 uppercase bg-gray-50 border-b">In Stock</th>
                <th className="p-2.5 text-right font-semibold text-xs text-gray-500 uppercase bg-gray-50 border-b">Shortfall</th>
                <th className="p-2.5 text-right font-semibold text-xs text-gray-500 uppercase bg-gray-50 border-b">Items</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((o, i) => (
                <React.Fragment key={o.order_id}>
                  <tr
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => toggleOrder(o.order_id)}
                    data-testid={`so-row-${i}`}
                  >
                    <td className="p-2.5 border-b border-gray-100 text-gray-400">
                      <span className={`inline-block transition-transform ${expanded[o.order_id] ? 'rotate-90' : ''}`}>&#9654;</span>
                    </td>
                    <td className="p-2.5 border-b border-gray-100">
                      <span className="font-semibold text-indigo-700">{o.so_number}</span>
                      {o.so_date && <span className="text-xs text-gray-400 ml-1.5">{o.so_date}</span>}
                    </td>
                    <td className="p-2.5 border-b border-gray-100">
                      <span className="font-semibold text-base text-gray-900">{o.customer_name}</span>
                    </td>
                    <td className="p-2.5 border-b border-gray-100 text-gray-600 text-xs">{o.agent_name || '—'}</td>
                    <td className="p-2.5 border-b border-gray-100 text-gray-600 text-xs">{o.distributor_name || '—'}</td>
                    <td className="p-2.5 border-b border-gray-100 text-right">
                      <Badge className={`text-xs ${o.days_since_order > 14 ? 'bg-red-100 text-red-800' : o.days_since_order > 7 ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-600'}`}>
                        {o.days_since_order}d
                      </Badge>
                    </td>
                    <td className="p-2.5 border-b border-gray-100 text-right font-mono font-semibold text-amber-700">{fmt(o.total_pending)}</td>
                    <td className="p-2.5 border-b border-gray-100 text-right font-mono text-emerald-600">{fmt(o.total_in_stock)}</td>
                    <td className="p-2.5 border-b border-gray-100 text-right font-mono font-bold text-red-600">{fmt(o.total_shortfall)}</td>
                    <td className="p-2.5 border-b border-gray-100 text-right text-gray-500">{o.items.length}</td>
                  </tr>
                  {expanded[o.order_id] && (
                    <tr>
                      <td colSpan={10} className="p-0 border-b border-gray-200">
                        <div className="bg-gray-50 px-6 py-3">
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="text-gray-500">
                                <th className="py-1.5 text-left font-semibold">Design</th>
                                <th className="py-1.5 text-left font-semibold">Brand</th>
                                <th className="py-1.5 text-left font-semibold">Vendor</th>
                                <th className="py-1.5 text-left font-semibold">Size</th>
                                <th className="py-1.5 text-left font-semibold">Color</th>
                                <th className="py-1.5 text-right font-semibold">Ordered</th>
                                <th className="py-1.5 text-right font-semibold">Dispatched</th>
                                <th className="py-1.5 text-right font-semibold">Pending</th>
                                <th className="py-1.5 text-right font-semibold">In Stock</th>
                                <th className="py-1.5 text-right font-semibold">Shortfall</th>
                              </tr>
                            </thead>
                            <tbody>
                              {o.items.map((item) =>
                                item.variants.map((v, vi) => (
                                  <tr key={`${item.item_id}-${vi}`} className="border-t border-gray-200">
                                    {vi === 0 && (
                                      <>
                                        <td rowSpan={item.variants.length} className="py-1.5 font-semibold text-gray-800">{item.design_code}</td>
                                        <td rowSpan={item.variants.length} className="py-1.5">{item.brand && <Badge className="bg-indigo-100 text-indigo-700 text-[10px]">{item.brand}</Badge>}</td>
                                        <td rowSpan={item.variants.length} className="py-1.5 text-gray-600">{item.vendor || '—'}</td>
                                      </>
                                    )}
                                    <td className="py-1.5"><Badge className="bg-blue-50 text-blue-700 text-[10px]">{v.size}</Badge></td>
                                    <td className="py-1.5 text-gray-600">{v.color}</td>
                                    <td className="py-1.5 text-right font-mono">{v.qty_ordered}</td>
                                    <td className="py-1.5 text-right font-mono text-gray-400">{v.qty_dispatched}</td>
                                    <td className="py-1.5 text-right font-mono font-semibold text-amber-700">{v.pending}</td>
                                    <td className="py-1.5 text-right font-mono text-emerald-600">{v.in_stock}</td>
                                    <td className={`py-1.5 text-right font-mono font-bold ${v.shortfall > 0 ? 'text-red-600' : 'text-emerald-600'}`}>{v.shortfall}</td>
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </table>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
              {/* Grand Total */}
              <tr className="bg-orange-50 font-bold">
                <td className="p-2.5" colSpan={6}><span className="text-orange-800">GRAND TOTAL ({filtered.length} SOs)</span></td>
                <td className="p-2.5 text-right font-mono text-orange-800">{fmt(filtered.reduce((a, o) => a + o.total_pending, 0))}</td>
                <td className="p-2.5 text-right font-mono text-orange-800">{fmt(filtered.reduce((a, o) => a + o.total_in_stock, 0))}</td>
                <td className="p-2.5 text-right font-mono text-orange-800">{fmt(filtered.reduce((a, o) => a + o.total_shortfall, 0))}</td>
                <td className="p-2.5"></td>
              </tr>
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

/* ======== Design-wise Table ======== */
const DesignWiseTable = ({ data, search, expanded, setExpanded, lightboxImg, setLightboxImg }) => {
  const API_BASE = process.env.REACT_APP_BACKEND_URL;
  const rows = data?.rows || [];
  const s = (search || '').toLowerCase();
  const filtered = rows.filter(r =>
    !s ||
    (r.design_code || '').toLowerCase().includes(s) ||
    (r.brand || '').toLowerCase().includes(s) ||
    (r.vendor || '').toLowerCase().includes(s)
  );

  const toggleDesign = (dc) => {
    setExpanded(prev => ({ ...prev, [dc]: !prev[dc] }));
  };

  if (filtered.length === 0) {
    return (
      <Card><CardContent className="p-12 text-center text-gray-400" data-testid="shortfall-empty">
        No designs with pending quantities found. All orders are fulfilled or no data matches the filters.
      </CardContent></Card>
    );
  }

  return (
    <Card data-testid="shortfall-design-table">
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr>
                <th className="p-2.5 text-left font-semibold text-xs text-gray-500 uppercase bg-gray-50 border-b w-8"></th>
                <th className="p-2.5 text-left font-semibold text-xs text-gray-500 uppercase bg-gray-50 border-b">#</th>
                <th className="p-2.5 text-center font-semibold text-xs text-gray-500 uppercase bg-gray-50 border-b w-12">Image</th>
                <th className="p-2.5 text-left font-semibold text-xs text-gray-500 uppercase bg-gray-50 border-b">Design</th>
                <th className="p-2.5 text-left font-semibold text-xs text-gray-500 uppercase bg-gray-50 border-b">Brand</th>
                <th className="p-2.5 text-left font-semibold text-xs text-gray-500 uppercase bg-gray-50 border-b">Vendor</th>
                <th className="p-2.5 text-right font-semibold text-xs text-gray-500 uppercase bg-gray-50 border-b">SOs</th>
                <th className="p-2.5 text-right font-semibold text-xs text-gray-500 uppercase bg-gray-50 border-b">Customers</th>
                <th className="p-2.5 text-right font-semibold text-xs text-gray-500 uppercase bg-gray-50 border-b">Ordered</th>
                <th className="p-2.5 text-right font-semibold text-xs text-gray-500 uppercase bg-gray-50 border-b">Dispatched</th>
                <th className="p-2.5 text-right font-semibold text-xs text-gray-500 uppercase bg-gray-50 border-b">Pending</th>
                <th className="p-2.5 text-right font-semibold text-xs text-gray-500 uppercase bg-gray-50 border-b">In Stock</th>
                <th className="p-2.5 text-right font-semibold text-xs text-gray-500 uppercase bg-gray-50 border-b">Shortfall</th>
                <th className="p-2.5 text-right font-semibold text-xs text-gray-500 uppercase bg-gray-50 border-b">Shortfall %</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r, i) => (
                <React.Fragment key={r.design_code}>
                  <tr
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => toggleDesign(r.design_code)}
                    data-testid={`design-row-${i}`}
                  >
                    <td className="p-2.5 border-b border-gray-100 text-gray-400">
                      <span className={`inline-block transition-transform ${expanded[r.design_code] ? 'rotate-90' : ''}`}>&#9654;</span>
                    </td>
                    <td className="p-2.5 border-b border-gray-100 text-gray-400">{i + 1}</td>
                    <td className="p-1.5 border-b border-gray-100 text-center">
                      {r.image ? (
                        <img src={`${API_BASE}${r.image}`} alt={r.design_code}
                          className="w-9 h-9 rounded object-cover cursor-pointer hover:ring-2 hover:ring-blue-400 transition-all mx-auto"
                          onClick={(e) => { e.stopPropagation(); setLightboxImg(`${API_BASE}${r.image}`); }} />
                      ) : (
                        <div className="w-9 h-9 bg-gray-100 rounded flex items-center justify-center text-gray-400 mx-auto text-[8px]">No img</div>
                      )}
                    </td>
                    <td className="p-2.5 border-b border-gray-100"><span className="font-bold text-gray-900">{r.design_code}</span></td>
                    <td className="p-2.5 border-b border-gray-100">{r.brand && <Badge className="bg-indigo-100 text-indigo-700 text-xs">{r.brand}</Badge>}</td>
                    <td className="p-2.5 border-b border-gray-100 text-gray-600">{r.vendor || '—'}</td>
                    <td className="p-2.5 border-b border-gray-100 text-right">{r.so_count}</td>
                    <td className="p-2.5 border-b border-gray-100 text-right">{r.customer_count}</td>
                    <td className="p-2.5 border-b border-gray-100 text-right font-mono">{fmt(r.total_ordered)}</td>
                    <td className="p-2.5 border-b border-gray-100 text-right font-mono text-gray-400">{fmt(r.total_dispatched)}</td>
                    <td className="p-2.5 border-b border-gray-100 text-right font-mono font-semibold text-amber-700">{fmt(r.total_pending)}</td>
                    <td className="p-2.5 border-b border-gray-100 text-right font-mono text-emerald-600">{fmt(r.total_in_stock)}</td>
                    <td className="p-2.5 border-b border-gray-100 text-right font-mono font-bold text-red-600">{fmt(r.total_shortfall)}</td>
                    <td className="p-2.5 border-b border-gray-100 text-right">
                      <Badge className={`text-xs ${r.shortfall_pct > 75 ? 'bg-red-100 text-red-800' : r.shortfall_pct > 25 ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-800'}`}>
                        {r.shortfall_pct}%
                      </Badge>
                    </td>
                  </tr>
                  {expanded[r.design_code] && r.size_breakdown && r.size_breakdown.length > 0 && (
                    <tr>
                      <td colSpan={13} className="p-0 border-b border-gray-200">
                        <div className="bg-gray-50 px-6 py-3">
                          <p className="text-xs font-semibold text-gray-500 mb-2">Size Breakdown</p>
                          <div className="flex flex-wrap gap-2">
                            {r.size_breakdown.map(sb => (
                              <div key={sb.size} className="bg-white border rounded-lg px-3 py-2 text-xs min-w-[120px]">
                                <Badge className="bg-blue-50 text-blue-700 text-[10px] mb-1">{sb.size}</Badge>
                                <div className="flex gap-3 mt-1">
                                  <span className="text-amber-700">Pending: <strong>{sb.pending}</strong></span>
                                  <span className="text-emerald-600">Stock: <strong>{sb.in_stock}</strong></span>
                                  <span className={sb.shortfall > 0 ? 'text-red-600' : 'text-emerald-600'}>Short: <strong>{sb.shortfall}</strong></span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
              {/* Grand Total */}
              <tr className="bg-orange-50 font-bold">
                <td className="p-2.5" colSpan={8}><span className="text-orange-800">GRAND TOTAL ({filtered.length} designs)</span></td>
                <td className="p-2.5 text-right font-mono text-orange-800">{fmt(filtered.reduce((a, r) => a + r.total_ordered, 0))}</td>
                <td className="p-2.5 text-right font-mono text-orange-800">{fmt(filtered.reduce((a, r) => a + r.total_dispatched, 0))}</td>
                <td className="p-2.5 text-right font-mono text-orange-800">{fmt(filtered.reduce((a, r) => a + r.total_pending, 0))}</td>
                <td className="p-2.5 text-right font-mono text-orange-800">{fmt(filtered.reduce((a, r) => a + r.total_in_stock, 0))}</td>
                <td className="p-2.5 text-right font-mono text-orange-800">{fmt(filtered.reduce((a, r) => a + r.total_shortfall, 0))}</td>
                <td className="p-2.5"></td>
              </tr>
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

export default Shortfall;
